import clsx from 'clsx'
import { PropertyValues, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { GenericFormElement } from '@/internal/mixin/genericFormElement'

import { ButtonShape, ButtonSize } from '../../common/types'
import styles from './_styles/toggle-button.scss?inline'

/**
 * `dap-ds-toggle-button` is a custom toggle button component.
 * @element dap-ds-toggle-button
 * @summary A toggle button is a button that can be toggled on (active) or off (inactive).
 * @title - Toggle button
 *
 * @slot - The content of the toggle button.
 *
 * @csspart base - The main button container.
 * @csspart high-contrast - The high contrast part of the button.
 *
 * @cssproperty --dds-toggle-button-min-width-lg - Minimum width of the large toggle button
 * @cssproperty --dds-toggle-button-min-width-md - Minimum width of the medium toggle button
 * @cssproperty --dds-toggle-button-min-width-sm - Minimum width of the small toggle button
 * @cssproperty --dds-toggle-button-min-width-xs - Minimum width of the extra small toggle button
 * @cssproperty --dds-toggle-button-height-lg - Height of the large toggle button
 * @cssproperty --dds-toggle-button-height-md - Height of the medium toggle button
 * @cssproperty --dds-toggle-button-height-sm - Height of the small toggle button
 * @cssproperty --dds-toggle-button-height-xs - Height of the extra small toggle button
 * @cssproperty --dds-toggle-button-padding-lg - Padding of the large toggle button
 * @cssproperty --dds-toggle-button-padding-md - Padding of the medium toggle button
 * @cssproperty --dds-toggle-button-padding-sm - Padding of the small toggle button
 * @cssproperty --dds-toggle-button-padding-xs - Padding of the extra small toggle button
 * @cssproperty --dds-toggle-button-font-size-lg - Font size of the large toggle button
 * @cssproperty --dds-toggle-button-font-size-md - Font size of the medium toggle button
 * @cssproperty --dds-toggle-button-font-size-sm - Font size of the small toggle button
 * @cssproperty --dds-toggle-button-font-size-xs - Font size of the extra small toggle button
 * @cssproperty --dds-toggle-button-border-enabled - Border color in enabled state
 * @cssproperty --dds-toggle-button-background-enabled - Background color in enabled state
 * @cssproperty --dds-toggle-button-content-enabled - Content/text color in enabled state
 * @cssproperty --dds-toggle-button-border-hover - Border color in hover state
 * @cssproperty --dds-toggle-button-background-hover - Background color in hover state
 * @cssproperty --dds-toggle-button-border-pressed - Border color in pressed state
 * @cssproperty --dds-toggle-button-background-pressed - Background color in pressed state
 * @cssproperty --dds-toggle-button-background-selected-enabled - Background color in selected enabled state
 * @cssproperty --dds-toggle-button-content-selected-enabled - Content/text color in selected enabled state
 * @cssproperty --dds-toggle-button-background-selected-hover - Background color in selected hover state
 * @cssproperty --dds-toggle-button-background-selected-pressed - Background color in selected pressed state
 * @cssproperty --dds-toggle-button-border-disabled - Border color in disabled state
 * @cssproperty --dds-toggle-button-background-disabled - Background color in disabled state
 * @cssproperty --dds-toggle-button-content-disabled - Content/text color in disabled state
 * @cssproperty --dds-toggle-button-highcontrast-border - High contrast border color
 * @cssproperty --dds-toggle-button-highcontrast-border-hover - High contrast border color on hover
 */
@customElement('dap-ds-toggle-button')
export default class DapDSToggleButton extends GenericFormElement {
  /** The size of the button
   * @type { 'lg' | 'md' | 'sm' | 'xs' }
   */
  @property({ reflect: true }) size: ButtonSize = 'md'

  /** Whether the button is active. */
  @property({ type: Boolean, reflect: true }) active = false

  /** The shape of the button
   * @type { 'button' | 'circle' }
   */
  @property({ reflect: true }) shape: ButtonShape = 'button'

  @property({ type: String }) sizeChildren = 'true'

  static readonly styles = unsafeCSS(styles)

  get focusElement(): HTMLButtonElement {
    return this.shadowRoot?.querySelector('button') as HTMLButtonElement
  }

  protected firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties)

    if (this.sizeChildren === 'true') {
      const slot = this.shadowRoot?.querySelector('slot')
      slot?.assignedElements().forEach(el => {
        el.setAttribute('size', this.size)
      })
    }
  }

  private handleClick(): void {
    if (this.disabled) {
      return
    }
  }

  render() {
    return html`
      <button
        part="base"
        .disabled=${this.disabled}
        aria-disabled=${this.disabled}
        aria-label=${ifDefined(this.ariaLabel) || ''}
        aria-pressed=${this.active}
        .autofocus=${this.autofocus}
        type="button"
        class=${clsx(
          'toggle-button',
          `toggle-button--${this.size}`,
          `toggle-button--${this.shape}`,
          {
            'toggle-button--active': this.active,
            'toggle-button--disabled': this.disabled,
          },
        )}
        @click=${this.handleClick}>
        <div part="high-contrast" class="toggle-button__highcontrast"></div>
        <slot></slot>
      </button>
    `
  }
}
